import { Button, Card, CardContent, cn, Skeleton } from '@up2stay/up2promo-ui';
import type { ProductBehavior } from '@up2stay/up2promo-api-client';

import {
  statusBoardCardClass,
  statusBoardCardPadClass,
  statusBoardMetaClass,
  statusBoardSectionTitleClass,
} from './dashboard-board-surfaces';
import type { DashboardLinkTargetRow } from './dashboard-status-types';

export type { DashboardLinkTargetRow };

export function DashboardLinkTargetsCard({
  links,
  isLoading,
  onManage,
  labels,
}: {
  links: DashboardLinkTargetRow[];
  isLoading: boolean;
  onManage: () => void;
  labels: {
    title: string;
    subtitle: string;
    manage: string;
    empty: string;
    footer: string;
    productsCount: string;
    typeLabels: Record<ProductBehavior, string>;
  };
}) {
  if (isLoading) {
    return (
      <Card className={cn(statusBoardCardClass, 'h-full')} aria-busy="true">
        <CardContent className={cn('space-y-3', statusBoardCardPadClass)}>
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <section
      aria-labelledby="dashboard-link-targets-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card className={cn(statusBoardCardClass, 'h-full')}>
        <CardContent
          className={cn(
            'flex h-full flex-col gap-3.5',
            statusBoardCardPadClass,
          )}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <h2
                id="dashboard-link-targets-heading"
                className={statusBoardSectionTitleClass}
              >
                {labels.title}
              </h2>
              <p className={cn('mt-0.5', statusBoardMetaClass)}>
                {labels.subtitle}
              </p>
            </div>
            <Button
              type="button"
              variant="ghostAccent"
              size="sm"
              className="h-8 shrink-0 px-2 text-[13px] font-medium"
              onClick={onManage}
            >
              {labels.manage}
            </Button>
          </div>

          {links.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              {labels.empty}
            </p>
          ) : (
            <ul className="flex flex-col">
              {links.map((link) => {
                const name = labels.typeLabels[link.type] ?? link.name;
                return (
                  <li
                    key={link.id}
                    className="flex items-center gap-3 border-t border-border/60 py-[9px] first:border-t-0 first:pt-0"
                  >
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-[9px] text-[12px] font-bold dark:bg-opacity-90"
                      style={{ background: link.tagBg, color: link.tagFg }}
                    >
                      {link.tag}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <div className="flex items-baseline gap-2">
                        <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-foreground">
                          {name}
                        </span>
                        <span className="font-mono text-[12px] tabular-nums text-foreground">
                          {labels.productsCount.replace(
                            '{count}',
                            String(link.productCount),
                          )}
                        </span>
                      </div>
                      <div className="h-1 overflow-hidden rounded-sm bg-muted">
                        <div
                          className="h-1 rounded-sm bg-[color:var(--brand-500)]"
                          style={{
                            width: `${Math.min(100, Math.max(link.share, 0))}%`,
                          }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="mt-auto text-[11px] text-muted-foreground md:text-[12px]">
            {labels.footer}
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
